import {
  array,
  minLength,
  minValue,
  nonEmpty,
  number,
  pipe,
  regex,
  safeInteger,
  safeParse,
  strictObject,
  string,
  trim,
  union,
} from "valibot";
import type { LockerversePaymentSelectionItem } from "./types.ts";

const productSlugSchema = pipe(string(), trim(), nonEmpty());
const positiveSafeIntegerSchema = pipe(number(), safeInteger(), minValue(1));

const paymentSelectionSchema = pipe(
  array(
    union([
      strictObject({
        amountCents: positiveSafeIntegerSchema,
        productSlug: productSlugSchema,
      }),
      strictObject({
        productSlug: productSlugSchema,
        quantity: positiveSafeIntegerSchema,
      }),
      strictObject({
        productSlug: productSlugSchema,
      }),
    ])
  ),
  minLength(1)
);

const customTipSchema = pipe(
  string(),
  trim(),
  regex(/^(?:0|[1-9]\d*)(?:\.\d{1,2})?$/, "Enter a valid tip amount.")
);

export function parseCustomTipCents(input: string) {
  const result = safeParse(customTipSchema, input);
  if (!result.success) {
    return {
      message: result.issues[0]?.message ?? "Enter a valid tip amount.",
      success: false,
    } as const;
  }
  const [dollars = "0", fraction = ""] = result.output.split(".");
  const cents = BigInt(dollars) * 100n + BigInt(fraction.padEnd(2, "0"));
  if (cents > BigInt(Number.MAX_SAFE_INTEGER)) {
    return { message: "Enter a valid tip amount.", success: false } as const;
  }
  return { cents: Number(cents), success: true } as const;
}

export function parsePaymentSelection(
  input: LockerversePaymentSelectionItem[]
) {
  const result = safeParse(paymentSelectionSchema, input);
  return result.success
    ? ({ success: true, value: result.output } as const)
    : ({
        message: "Lockerverse payment configuration is invalid.",
        success: false,
      } as const);
}
